/**
 * On-screen keyboard tracking for mobile bottom-sheets.
 *
 * iOS Safari does NOT shrink the layout viewport (or `100dvh`/`100vh`) when the
 * software keyboard opens — it overlays the keyboard on top of the page. So a
 * `position: fixed; bottom: 0` bottom-sheet keeps its full height and the
 * keyboard hides its footer (Create / Cancel) and any lower input. That is the
 * "the keyboard covers the whole modal, I can't see what I'm typing or reach the
 * buttons" bug.
 *
 * Fix: measure the overlap between the layout viewport and the (keyboard-shrunk)
 * VisualViewport and publish it as the CSS custom property `--kb-inset` on
 * <html>. Bottom-sheets read it to sit fully ABOVE the keyboard:
 *   bottom: var(--kb-inset);  max-height: calc(<h> - var(--kb-inset));
 *
 * overlap = innerHeight - visualViewport.height - visualViewport.offsetTop
 * This is 0 when no keyboard is up. It's also ~0 on browsers/OS versions where
 * the keyboard already resizes the layout viewport, so there's never a double
 * offset — the measurement self-corrects.
 */
export function setupKeyboardInset(): void {
  if (typeof window === 'undefined') return;
  const vv = window.visualViewport;
  const root = document.documentElement;
  if (!vv) {
    // No VisualViewport API (very old browsers) — leave the default 0.
    root.style.setProperty('--kb-inset', '0px');
    return;
  }

  let raf = 0;
  const update = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // Ignore tiny overlaps (browser-chrome rounding) so a closed keyboard
      // reliably reads as 0 and the sheet doesn't jitter.
      root.style.setProperty('--kb-inset', `${overlap > 24 ? Math.round(overlap) : 0}px`);
    });
  };

  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  update();
}
